import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaSave } from 'react-icons/fa';

import { apiService } from '../../components/Config/api.service';
import HeaderCard from '../../components/Header/HeaderCard';
import LoadingBarWithStyles from '../../components/Utils/LoadingBarWithStyles';

/**
 * Le circuit de validation, réglé par type de demande (Yros/Kavero#468).
 *
 * Qui valide avant la RH : personne (« RH seule »), le manager seul, ou le
 * manager puis la RH. Le serveur livre, pour chaque type, le circuit en
 * vigueur et les seuls choix permis — ce qui touche la paie finit par la RH ;
 * l'écran n'en invente aucun. Un type sans alternative se lit sans liste.
 *
 * Un bouton par ligne, actif seulement quand la ligne a changé : un réglage ne
 * part jamais tout seul. Un réglage ne change rien aux demandes déjà déposées,
 * l'explication le dit. Le journal dit qui a changé quoi.
 */

// Le message du serveur, ou la clé du texte générique : `t` ne vit pas dans
// les effets de chargement, qui ne tournent qu'une fois.
const GENERIQUE = 'circuitsValidation.erreurGenerique';
const message = (e) => e?.response?.data?.message || GENERIQUE;

function CircuitsValidation() {
  const { t } = useTranslation();
  const [circuits, setCircuits] = useState(null);
  const [choisis, setChoisis] = useState({});
  const [journal, setJournal] = useState([]);
  const [envoi, setEnvoi] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [succes, setSucces] = useState(null);

  const lireJournal = useCallback(async () => {
    try {
      const reponse = await apiService.lireJournalCircuits();
      if (reponse?.ok) setJournal(reponse.journal || []);
    } catch (e) {
      // Le journal est un complément : sa panne ne masque pas le réglage.
    }
  }, []);

  useEffect(() => {
    let actif = true;
    (async () => {
      try {
        const reponse = await apiService.lireCircuitsValidation();
        if (!actif) return;
        if (reponse?.ok) {
          setCircuits(reponse.circuits || []);
          setChoisis(Object.fromEntries((reponse.circuits || []).map((c) => [c.type, c.circuit])));
        } else {
          setErreur(reponse?.message || GENERIQUE);
        }
      } catch (e) {
        if (actif) setErreur(message(e));
      }
    })();
    lireJournal();
    return () => { actif = false; };
  }, [lireJournal]);

  const enregistrer = async (type) => {
    setErreur(null);
    setSucces(null);
    setEnvoi(type);
    try {
      const reponse = await apiService.reglerCircuitValidation(type, choisis[type]);
      if (reponse?.ok) {
        setCircuits((liste) => liste.map((c) => (c.type === type ? reponse.circuit : c)));
        setSucces(t('circuitsValidation.succes', { type: t(`circuitsValidation.types.${type}`) }));
        lireJournal();
      } else {
        setErreur(reponse?.message || GENERIQUE);
      }
    } catch (e) {
      setErreur(message(e));
    } finally {
      setEnvoi(null);
    }
  };

  return (
    <div className="p-4 space-y-4">
      <HeaderCard title={t('circuitsValidation.titre')} text={t('circuitsValidation.explication')} />

      {erreur && (
        <p data-testid="circuits-erreur" role="alert"
          className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
          {erreur === GENERIQUE ? t(GENERIQUE) : erreur}
        </p>
      )}
      {succes && (
        <p data-testid="circuits-succes"
          className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-4 py-3">
          {succes}
        </p>
      )}

      {circuits === null && !erreur && <LoadingBarWithStyles />}

      {circuits && (
        <ul className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100">
          {circuits.map(({ type, circuit, choix }) => (
            <li key={type} className="flex flex-col sm:flex-row sm:items-center gap-3 p-4">
              <span className="sm:w-56 font-medium text-gray-800">{t(`circuitsValidation.types.${type}`)}</span>
              {choix.length > 1 ? (
                <>
                  <select
                    data-testid={`circuit-${type}`}
                    aria-label={t(`circuitsValidation.types.${type}`)}
                    value={choisis[type]}
                    onChange={(e) => setChoisis((c) => ({ ...c, [type]: e.target.value }))}
                    className="flex-1 px-3 py-2 border rounded-lg text-sm text-gray-700 bg-gray-50"
                  >
                    {choix.map((valeur) => (
                      <option key={valeur} value={valeur}>{t(`circuitsValidation.circuits.${valeur}`)}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    data-testid={`circuit-enregistrer-${type}`}
                    disabled={choisis[type] === circuit || envoi !== null}
                    onClick={() => enregistrer(type)}
                    className="flex items-center justify-center gap-2 px-4 py-2 text-sm bg-[--color-primary] text-white rounded-lg hover:bg-[--bg-hover] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <FaSave size={12} aria-hidden="true" />
                    {envoi === type ? t('circuitsValidation.envoi') : t('circuitsValidation.enregistrer')}
                  </button>
                </>
              ) : (
                <span data-testid={`circuit-fixe-${type}`} className="flex-1 text-sm text-gray-600">
                  {t(`circuitsValidation.circuits.${circuit}`)}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}

      {circuits && (
        <section className="space-y-2">
          <h3 className="text-sm font-semibold text-gray-700">{t('circuitsValidation.journalTitre')}</h3>
          {journal.length === 0 ? (
            <p className="text-sm text-gray-500">{t('circuitsValidation.journalVide')}</p>
          ) : (
            <ul className="text-sm text-gray-600 space-y-1">
              {journal.map((ligne, index) => (
                <li key={`${ligne.date}-${index}`} data-testid={`circuits-journal-${index}`}>
                  <span className="text-gray-400">{ligne.date}</span>
                  {' — '}
                  <span className="font-medium">{ligne.par}</span>
                  {' : '}
                  {t(`circuitsValidation.types.${ligne.type}`)}
                  {', '}
                  {t(`circuitsValidation.circuits.${ligne.avant}`)}
                  {' → '}
                  {t(`circuitsValidation.circuits.${ligne.apres}`)}
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
    </div>
  );
}

export default CircuitsValidation;
